import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ChevronLeft, ChevronRight, Wallet, Coins, TrendingUp,
  TrendingDown, Clock, Loader2, Banknote, PiggyBank,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCHF = (n) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const WAGE_LABEL = { hourly: 'Stundenlohn', fixed: 'Fixlohn' };

const KpiCard = ({ icon: Icon, label, value, sub, color, bg, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${bg}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const TrainerProfitabilityPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const now = new Date();

  const [year, setYear] = useState(
    parseInt(params.get('year'), 10) || now.getFullYear()
  );
  const [month, setMonth] = useState(
    parseInt(params.get('month'), 10) || now.getMonth() + 1
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/trainers/${id}/profitability?year=${year}&month=${month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (e) {
      setError('Rentabilitätsdaten konnten nicht geladen werden.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m);
    setYear(y);
  };

  const profitPositive = (data?.profit_full ?? 0) >= 0;
  const avgProfitPositive = (data?.averages?.profit_full ?? 0) >= 0;
  const wageType = data?.trainer?.wage_type;
  const wageSub =
    wageType === 'hourly'
      ? `${WAGE_LABEL.hourly}: ${fmtCHF(data?.trainer?.hourly_wage)} × ${data?.hours ?? 0} Std.`
      : wageType === 'fixed'
      ? `${WAGE_LABEL.fixed} (unabhängig von Stunden)`
      : 'Kein Lohn hinterlegt';
  const costSub = data
    ? `Lohn ${fmtCHF(data.cost)} · AG ${fmtCHF(data.employer_social)} · PK ${fmtCHF(data.employer_pk)}`
    : '';

  return (
    <div className="space-y-6" data-testid="trainer-profitability-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            data-testid="trainer-profitability-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="trainer-profitability-name">
              {data?.trainer?.name || 'Trainer'}
            </h1>
            <p className="text-sm text-gray-500">
              Rentabilität · Einnahmen vs. Kosten
              {wageType && (
                <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                  {WAGE_LABEL[wageType] || wageType}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Monatsfilter */}
        <div className="flex items-center gap-2" data-testid="trainer-month-filter">
          <Button variant="outline" size="icon" onClick={() => shiftMonth(-1)} data-testid="trainer-month-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-[150px] text-center font-semibold text-gray-800" data-testid="trainer-month-label">
            {data?.period?.label || `${month}/${year}`}
          </div>
          <Button variant="outline" size="icon" onClick={() => shiftMonth(1)} data-testid="trainer-month-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="py-12 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center text-red-500" data-testid="trainer-profitability-error">
            {error}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={Wallet}
              label="Einnahmen (IST)"
              value={fmtCHF(data.revenue?.total_ist)}
              sub={`Geld ${fmtCHF(data.revenue?.money_ist)} · Punkte ${fmtCHF(data.revenue?.points_ist)}`}
              color="text-emerald-600"
              bg="bg-emerald-50"
              testId="trainer-kpi-revenue"
            />
            <KpiCard
              icon={Banknote}
              label="Vollkosten"
              value={fmtCHF(data.full_cost)}
              sub={costSub}
              color="text-orange-600"
              bg="bg-orange-50"
              testId="trainer-kpi-cost"
            />
            <KpiCard
              icon={profitPositive ? TrendingUp : TrendingDown}
              label="Gewinn (Vollkosten)"
              value={fmtCHF(data.profit_full)}
              sub={`Einnahmen − Vollkosten · Gewinn nur Lohn: ${fmtCHF(data.profit)}`}
              color={profitPositive ? 'text-green-600' : 'text-red-600'}
              bg={profitPositive ? 'bg-green-50' : 'bg-red-50'}
              testId="trainer-kpi-profit"
            />
            <KpiCard
              icon={Clock}
              label="Geleistete Stunden"
              value={`${data.hours ?? 0} Std.`}
              sub={`${data.revenue?.trainings_attended ?? 0} von ${data.revenue?.trainings_total ?? 0} Trainings wahrgenommen`}
              color="text-sky-600"
              bg="bg-sky-50"
              testId="trainer-kpi-hours"
            />
          </div>

          {/* Durchschnitt */}
          <Card data-testid="trainer-averages">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <PiggyBank className="w-5 h-5 text-gray-700" />
                Durchschnitt pro Monat
                <span className="text-[11px] font-normal text-gray-400">
                  (letzte {data.averages?.months || 0} aktive Monate)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-lg bg-emerald-50 p-4" data-testid="trainer-avg-revenue">
                  <p className="text-xs text-gray-500">Ø Einnahmen</p>
                  <p className="text-xl font-bold text-emerald-600">{fmtCHF(data.averages?.revenue)}</p>
                </div>
                <div className="rounded-lg bg-orange-50 p-4" data-testid="trainer-avg-cost">
                  <p className="text-xs text-gray-500">Ø Vollkosten</p>
                  <p className="text-xl font-bold text-orange-600">{fmtCHF(data.averages?.full_cost)}</p>
                </div>
                <div className={`rounded-lg p-4 ${avgProfitPositive ? 'bg-green-50' : 'bg-red-50'}`} data-testid="trainer-avg-profit">
                  <p className="text-xs text-gray-500">Ø Gewinn (Vollkosten)</p>
                  <p className={`text-xl font-bold ${avgProfitPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {fmtCHF(data.averages?.profit_full)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 12-Monats-Historie */}
          <Card data-testid="trainer-history">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Coins className="w-5 h-5 text-gray-700" />
                Verlauf (12 Monate)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]" data-testid="trainer-history-table">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-3 font-medium text-left">Monat</th>
                      <th className="py-2 px-2 font-medium text-right">Einnahmen</th>
                      <th className="py-2 px-2 font-medium text-right">Lohn</th>
                      <th className="py-2 px-2 font-medium text-right">Vollkosten</th>
                      <th className="py-2 px-2 font-medium text-right">Gewinn</th>
                      <th className="py-2 pl-2 font-medium text-right">Stunden</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.history || []).slice().reverse().map((h) => {
                      const isCurrent = h.year === year && h.month === month;
                      const pos = (h.profit_full ?? 0) >= 0;
                      return (
                        <tr
                          key={`${h.year}-${h.month}`}
                          onClick={() => { setYear(h.year); setMonth(h.month); }}
                          className={`border-b border-gray-50 cursor-pointer transition-colors hover:bg-blue-50 ${isCurrent ? 'bg-blue-50/60 font-semibold' : ''}`}
                          data-testid={`trainer-history-row-${h.year}-${h.month}`}
                        >
                          <td className="py-2 pr-3 text-left text-gray-800">{h.label}</td>
                          <td className="py-2 px-2 text-right text-emerald-700">{fmtCHF(h.revenue?.total_ist)}</td>
                          <td className="py-2 px-2 text-right text-gray-500">{fmtCHF(h.cost)}</td>
                          <td className="py-2 px-2 text-right text-orange-700">{fmtCHF(h.full_cost)}</td>
                          <td className={`py-2 px-2 text-right font-medium ${pos ? 'text-green-700' : 'text-red-600'}`}>{fmtCHF(h.profit_full)}</td>
                          <td className="py-2 pl-2 text-right text-gray-600">{h.hours ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                Einnahmen = wahrgenommene Trainings (Geld + Punkte in CHF), unabhängig vom Bezahlstatus der Rechnung.
                Vollkosten = Lohn (Stundenlohn × Ist-Stunden oder Fixlohn) + Arbeitgeberanteile (AHV/ALV auf den Bruttolohn) + PK (pro Mitarbeiter).
                Gewinn = Einnahmen − Vollkosten.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default TrainerProfitabilityPage;
